/**
 * T-0892 (T-0770): where a store that only some screens use lives, so the launch entry does not carry it.
 *
 * `workItemStoreSlot.ts` did this for the work-item store; these are the small ones that followed
 * (comments, AI suggestions, feedback, labels, open branches), so there is one shape for them. A slot holds
 * the store once its `*StoreInstance.ts` module has loaded. The root store's getter for it reads the slot and
 * throws {@link StoreNotLoadedError} before then: a screen that reads the store imports the instance module
 * statically (`import '@/stores/commentStoreInstance'`), so the getter always finds it. `lazyStores.guard.test.ts`
 * holds that for every file, and keeps these reads out of the launch entry.
 */
export class StoreNotLoadedError extends Error {
  constructor(name: string) {
    super(
      `The ${name} was read before it loaded: import its "*StoreInstance" module in the screen that reads it, ` +
        "or await its slot's load() (T-0892).",
    );
    this.name = 'StoreNotLoadedError';
  }
}

export interface IStoreSlot<T> {
  /** Called once, by the instance module, with the store it built. */
  provide(store: T): void;
  /** The store if it has loaded, else undefined. */
  peek(): T | undefined;
  /** The store, loading it on first call; one load, shared by every caller. */
  load(): Promise<T>;
  /** The store, or {@link StoreNotLoadedError}. */
  orThrow(): T;
}

export function createStoreSlot<T>(name: string, importStore: () => Promise<T>): IStoreSlot<T> {
  let instance: T | undefined;
  let loading: Promise<T> | null = null;
  return {
    provide(store) {
      instance = store;
    },
    peek: () => instance,
    load() {
      if (instance) return Promise.resolve(instance);
      loading ??= importStore().catch((error: unknown) => {
        // A failed load (a deployment replaced under the tab) is tried again by the next caller.
        loading = null;
        throw error;
      });
      return loading;
    },
    orThrow() {
      if (!instance) throw new StoreNotLoadedError(name);
      return instance;
    },
  };
}
